<script setup>
import {
  ref,
  nextTick,
  onMounted,
  onBeforeUnmount,
  computed,
  watch,
  defineAsyncComponent,
} from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import SectionCard from 'dashboard/components-next/section-card/SectionCard.vue';
import { formatDateParam } from 'shared/helpers/DateHelper';
import { formatTime } from 'shared/helpers/formatTime';
import { buildOrderedStatusOptions } from './statusOptions';
import { useEventPricing } from './useEventPricing';
import { useEventQuotes } from './useEventQuotes';
import { useEventDocuments } from './useEventDocuments';
import {
  computeSuggestedPricing,
  suggestedRateQuote,
} from './suggestedPricing';
import {
  quotedPriceBounds,
} from './eventListPrice';
import { useEventHistory } from './useEventHistory';
import { useEventSharing } from './useEventSharing';
import { useEventFloorplans } from './useEventFloorplans';

import { useEventAllocations } from './useEventAllocations';
import { useAddEventSlot } from './useAddEventSlot';
import { useEventCopyMarkdown } from './useEventCopyMarkdown';
import { useEventSectionCollapse } from './useEventSectionCollapse';
import {
  MAIN_COLUMN_FIXED_SECTION_KEYS,
  reconcileSectionOrder,
  useEventSectionOrder,
} from './useEventSectionOrder';
import { useEventCustomAttributeSections } from './useEventCustomAttributeSections';
import {
  applyPlanningEventPatch,
  isOlderPlanningEvent,
} from './mergePlanningEvent';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { useEmitter } from 'dashboard/composables/emitter';
import {
  categoryFromSectionKey,
  customAttrSectionKey,
  isCustomAttrSectionKey,
} from './eventSectionKeys';
import Draggable from 'vuedraggable';
import { useConfetti } from 'dashboard/composables/useConfetti';
import { useAdmin } from 'dashboard/composables/useAdmin';
import EventShowHeader from './components/EventShowHeader.vue';
import EventKeyInfo from './components/EventKeyInfo.vue';
import EventCustomAttributeBlock from './components/EventCustomAttributeBlock.vue';
import EventCustomAttributeAddBlock from './components/EventCustomAttributeAddBlock.vue';
import EventPricingSection from './components/EventPricingSection.vue';
import EventQuotesSection from './components/EventQuotesSection.vue';
import EventDocumentsSection from './components/EventDocumentsSection.vue';
import DocumentDropOverlay from './DocumentDropOverlay.vue';
import EventSchedule from './components/EventSchedule.vue';

import EventContactInfo from './components/EventContactInfo.vue';
import EventMembersInfo from './components/EventMembersInfo.vue';
import EventSuppliersInfo from './components/EventSuppliersInfo.vue';
import EventContactConversations from './components/EventContactConversations.vue';
import EventPricingSidebar from './components/EventPricingSidebar.vue';
import EventShijiBookingCard from './components/EventShijiBookingCard.vue';
import EventShareDialog from './components/EventShareDialog.vue';
import EventChangelogDialog from './components/EventChangelogDialog.vue';
import EventChangelogSidebar from './components/EventChangelogSidebar.vue';
import { useShijiBookingLink } from 'dashboard/composables/useShijiBookingLink';
import { useLostStageUpdate } from './useLostStageUpdate';
import { useEventKeyInfoEditing } from './useEventKeyInfoEditing';
import { resolveEventAccentColor } from './useEventStageColors';
import { CUSTOM_COLOR_SWATCHES } from 'dashboard/components-next/planning/Scheduler/allocationCategories';
import { useAlert } from 'dashboard/composables';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useAccount } from 'dashboard/composables/useAccount';
import { accountCurrency } from 'dashboard/helper/currency';
import SellToNetworkModal from 'dashboard/routes/dashboard/leadNetwork/SellToNetworkModal.vue';
import LeadNetworkAPI from 'dashboard/api/leadNetwork';
import {
  openQuotesForAllocation,
  quoteDisplayLabel,
} from './openQuotesForAllocation';

const props = defineProps({
  eventId: { type: String, default: null },
});

const EventGeneralInfo = defineAsyncComponent(
  () => import('./EventGeneralInfo.vue')
);
const QuoteBuilderModal = defineAsyncComponent(
  () => import('./components/QuoteBuilderModal.vue')
);
const QuoteSendDialog = defineAsyncComponent(
  () => import('./components/QuoteSendDialog.vue')
);
const EventFloorplanSection = defineAsyncComponent(
  () => import('./components/EventFloorplanSection.vue')
);
const EventTimeslotModal = defineAsyncComponent(
  () => import('./components/EventTimeslotModal.vue')
);
const EventLostCancellationModal = defineAsyncComponent(
  () => import('./EventLostCancellationModal.vue')
);
const EventRescheduleModal = defineAsyncComponent(
  () => import('./components/EventRescheduleModal.vue')
);
const EventDuplicateModal = defineAsyncComponent(
  () => import('./components/EventDuplicateModal.vue')
);

const store = useStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const { triggerConfetti } = useConfetti();
const { isAdmin } = useAdmin();
const { accountId, isCloudFeatureEnabled, currentAccount } = useAccount();
const showSellToNetworkModal = ref(false);
const leadNetworkMemberships = ref([]);
const {
  showLostCancellationModal,
  pendingLostUpdate,
  lostCancellationSaving,
  updateEventStage,
  confirmLostCancellation,
  cancelLostCancellation,
} = useLostStageUpdate();

const quotesEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.PLANNING_QUOTES)
);

const documentsEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.PLANNING_EVENT_DOCUMENTS)
);

const suppliersEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.SUPPLIERS)
);

const canSellToNetwork = computed(
  () =>
    isCloudFeatureEnabled(FEATURE_FLAGS.LEAD_NETWORKS) &&
    leadNetworkMemberships.value.length > 0 &&
    event.value?.conversation_id &&
    ['inquiry', 'lost'].includes(event.value?.system_stage)
);

// ── Core State ──
const loading = ref(true);
const event = ref(null);
let readTimer = null;
const deleteDialogRef = ref(null);
const deleteSlotDialogRef = ref(null);
const pendingDeleteAllocation = ref(null);
const timeslotModalOpen = ref(false);
const showRescheduleModal = ref(false);
const rescheduleSaving = ref(false);
const showDuplicateModal = ref(false);
const duplicateSaving = ref(false);
const editingTimeslot = ref(null);
const shareDialogRef = ref(null);
const editingName = ref(false);
const showEventManagerDropdown = ref(false);
const sheetScrollRef = ref(null);
const showCustomerDropdown = ref(false);
const tempName = ref('');
const saving = ref(false);
const deletingAllocationId = ref(null);
const searchingContacts = ref(false);
const searchingAgents = ref(false);
const statusOptions = ref([]);
const generalInfoContent = ref('');
const generalInfoEventId = ref(null);

const descriptionForOpenEvent = planningEvent => {
  if (
    planningEvent &&
    generalInfoEventId.value &&
    String(generalInfoEventId.value) === String(planningEvent.id)
  ) {
    return generalInfoContent.value;
  }
  return planningEvent?.description || '';
};

const persistDescription = useDebounceFn(async (html, id) => {
  if (!id) return;
  try {
    await store.dispatch('planningEvents/update', {
      id,
      description: html,
    });
  } catch {
    // Error handled by store
  }
}, 800);

const saveDescription = html => {
  persistDescription(html, event.value?.id);
};

const eventLabels = computed(
  () => store.getters['planningEventLabels/getRecords']
);

const eventId = computed(() => props.eventId || route.params.eventId);
const shijiBookingLink = useShijiBookingLink(event);

const accessLevel = computed(() => event.value?.access_level || 'read');
const canEditEvent = computed(
  () =>
    isAdmin.value ||
    accessLevel.value === 'full' ||
    accessLevel.value === 'collaborator'
);
const canManageEvent = computed(
  () => isAdmin.value || accessLevel.value === 'full'
);
const canViewPricing = computed(() => canManageEvent.value);
const canManageQuotes = computed(() => canManageEvent.value);
const canDeleteEvent = computed(() => canManageEvent.value);
const canChangeStage = computed(() => canManageEvent.value);
const editableCategoryKeys = computed(
  () =>
    event.value?.editable_category_keys ||
    event.value?.visible_category_keys ||
    []
);
// Partial category assignees stay read-only for event-level fields; category
// blocks use isCategoryReadOnly + editableCategoryKeys to unlock their categories.
const isReadOnly = computed(() => !canEditEvent.value);
const collaboratorIds = computed(() => event.value?.collaborator_ids || []);
const assignedTeamIds = computed(() => event.value?.team_ids || []);
const supplierIds = computed(() => event.value?.supplier_ids || []);
const assignedSuppliers = computed(() => event.value?.suppliers || []);

// ── Presence / Viewers ──
const currentUser = computed(() => store.getters.getCurrentUser);
const canManageCollaborators = computed(
  () =>
    canManageEvent.value ||
    Number(event.value?.event_manager_id) === Number(currentUser.value?.id)
);
const viewers = computed(() => {
  const all = store.getters['planningEventViewingStatus/getViewers'](
    eventId.value
  );
  // Exclude self from the viewer list
  return all.filter(v => v.id !== currentUser.value?.id);
});

// ── Composables ──
const allAgents = computed(() => store.getters['agents/getAgents']);
const allContacts = computed(() => store.getters['contacts/getContacts']);

const {
  selectedPlanId,
  selectedPlan,
  plans: pricingPlans,
  breakdown: pricingBreakdown,
  displayTotal: pricingDisplayTotal,
  displayBreakdown: pricingDisplayBreakdown,
  loadingPrice,
  loadPlans: loadPricingPlans,
  loadFromEvent,
  fetchBreakdown: fetchPricingBreakdown,
  switchPlan,
  reset: resetPricing,
  isLocked: pricingIsLocked,
  locking: lockingPrice,
  lockPrice,
  unlockPrice,
  priceDelta,
} = useEventPricing(eventId, { quotesEnabled });

const {
  quotes,
  loading: quotesLoading,
  filter: quotesFilter,
  filteredQuotes,
  planLocked,
  selectedQuoteId,
  compareQuoteId,
  compareQuote,
  showBuilder,
  builderSeedQuote,
  showSendDialog,
  sendQuoteTarget,
  fetchQuotes,
  refreshQuotes,
  openBuilder,
  closeBuilder,
  createQuote,
  updateQuote,
  reviseQuote,
  archiveQuote,
  sendQuote,
  closeSendDialog,
  onQuoteSent,
  withdrawQuote,
  acceptQuote,
  rejectQuote,
  setCompareQuote,
  reset: resetQuotes,
} = useEventQuotes(eventId);

const {
  documents: eventDocuments,
  loading: eventDocumentsLoading,
  library: eventDocumentLibrary,
  libraryLoading: eventDocumentLibraryLoading,
  fetchDocuments,
  fetchLibrary: fetchDocumentLibrary,
  attachDocument,
  updateDocument,
  removeDocument,
  voidDocument,
} = useEventDocuments(eventId);

const selectedDocumentLibraryId = ref(null);

watch(eventDocumentLibrary, library => {
  if (!library?.knowledgebase_id) return;
  if (selectedDocumentLibraryId.value == null) {
    selectedDocumentLibraryId.value = library.knowledgebase_id;
  }
});

const documentDropActive = ref(false);
const documentUploading = ref(false);
let documentDragDepth = 0;

const isFileDrag = event =>
  Array.from(event.dataTransfer?.types || []).includes('Files');

const documentsSectionVisible = computed(
  () =>
    documentsEnabled.value &&
    eventSectionItems.value.some(item => item.key === 'documents')
);

const canDropEventDocuments = computed(
  () =>
    documentsSectionVisible.value &&
    !isReadOnly.value &&
    !documentUploading.value
);

const handleAttachDocument = async payload => {
  documentUploading.value = true;
  try {
    const recordIds = payload?.record_ids || [];
    if (recordIds.length) {
      await Promise.all(
        recordIds.map(recordId =>
          attachDocument({ record_id: recordId }).catch(e => {
            useAlert(
              e?.response?.data?.error ||
                t('PLANNING.SHOW.DOCUMENTS_ATTACH_ERROR')
            );
            return null;
          })
        )
      );
      return;
    }
    await attachDocument(payload);
  } catch (e) {
    useAlert(
      e?.response?.data?.error || t('PLANNING.SHOW.DOCUMENTS_ATTACH_ERROR')
    );
    throw e;
  } finally {
    documentUploading.value = false;
  }
};

const onDocumentDragEnter = event => {
  if (!canDropEventDocuments.value || !isFileDrag(event)) return;
  event.preventDefault();
  documentDragDepth += 1;
  documentDropActive.value = true;
};

const onDocumentDragOver = event => {
  if (!canDropEventDocuments.value || !isFileDrag(event)) return;
  event.preventDefault();
  if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
};

const onDocumentDragLeave = event => {
  if (!documentDropActive.value) return;
  event.preventDefault();
  documentDragDepth = Math.max(0, documentDragDepth - 1);
  if (documentDragDepth === 0) documentDropActive.value = false;
};

const onDocumentDrop = async event => {
  if (!canDropEventDocuments.value || !isFileDrag(event)) return;
  event.preventDefault();
  documentDragDepth = 0;
  documentDropActive.value = false;

  const files = Array.from(event.dataTransfer?.files || []);
  if (!files.length) return;

  if (isSectionCollapsed('documents').value) {
    toggleSection('documents');
  }

  documentUploading.value = true;
  try {
    await Promise.all(
      files.map(file =>
        attachDocument({ file, requires_signature: false }).catch(e => {
          useAlert(
            e?.response?.data?.error || t('PLANNING.SHOW.DOCUMENTS_ATTACH_ERROR')
          );
          return null;
        })
      )
    );
  } finally {
    documentUploading.value = false;
  }
};

const quoteBuilderSaving = ref(false);

const saveQuoteFromBuilder = async payload => {
  quoteBuilderSaving.value = true;
  try {
    const seed = builderSeedQuote.value;
    if (!seed?.id) {
      await createQuote(payload);
      useAlert(t('PLANNING.SHOW.QUOTE_CREATED'));
    } else if (seed.status === 'not_sent') {
      await updateQuote(seed.id, payload);
      useAlert(t('PLANNING.SHOW.QUOTE_SAVED'));
    } else {
      await reviseQuote(seed.id, payload);
      useAlert(t('PLANNING.SHOW.QUOTE_CREATED'));
    }
    closeBuilder();
  } catch (e) {
    useAlert(e?.response?.data?.error || t('PLANNING.SHOW.QUOTE_CREATE_ERROR'));
  } finally {
    quoteBuilderSaving.value = false;
  }
};

const handleAcceptQuote = async (id, acceptanceReason) => {
  await acceptQuote(id, acceptanceReason);
  const updated = await fetchEvent();
  if (updated) await fetchPricingBreakdown();
};

const handleArchiveQuote = async id => {
  try {
    await archiveQuote(id);
    useAlert(t('PLANNING.SHOW.QUOTE_DELETED'));
  } catch (e) {
    useAlert(
      e?.response?.data?.error || t('PLANNING.SHOW.QUOTE_DELETE_ERROR')
    );
  }
};

const {
  historyLoading,
  historyError,
  historyRecords,
  historyMeta,
  isHistoryDialogOpen,
  openHistoryDialog,
  closeHistoryDialog,
  onHistoryPageChange,
  getAuditChanges,
  actionLabel,
  actionBadgeClass,
} = useEventHistory(eventId);

const {
  sharingEnabled,
  shareUrl,
  copyingLink,
  initFromEvent: initSharingFromEvent,
  toggleSharing,
  copyShareLink,
  regenerateLink,
} = useEventSharing(eventId);

// ── Event Page Templates ──
const eventPortals = computed(
  () => store.getters['planningEventPortals/getRecords'] || []
);
const selectedTemplateId = computed(() => event.value?.event_portal_id || null);
const selectTemplate = async templateId => {
  const eid = eventId.value;
  await store.dispatch('planningEvents/update', {
    id: eid,
    event_portal_id: templateId,
  });
  event.value = { ...event.value, event_portal_id: templateId };
};

const {
  showFloorplanPicker,
  floorplanPickerStep,
  selectedPickerSpace,
  pickerLayouts,
  loadingPickerLayouts,
  eventFloorplans,
  loadingFloorplans,
  creatingFloorplan,
  availableSpacesForFloorplan,
  fetchEventFloorplans,
  openFloorplanPicker,
  closeFloorplanPicker,
  selectPickerSpace,
  attachFloorplan,
  deleteEventFloorplan,
  openEventFloorplanEditor,
} = useEventFloorplans(eventId, event);

const {
  allocationDateRange,
  childAllocations,
  isAllocationDisco,
  getResourceName,
  formatAllocationTime,
} = useEventAllocations(event);

const planningResources = computed(
  () => store.getters['planningResources/getAllocatableResources']
);
const accountSpaces = computed(() => store.getters['spaces/getSpaces']);

const { addingSlot, duplicateSlot, saveTimeslot } = useAddEventSlot({
  eventId,
  event,
  spaces: accountSpaces,
  resources: planningResources,
});

const {
  editingDate,
  editingTime,
  editingLocation,
  tempStartDate,
  tempStartTime,
  tempEndDate,
  tempEndTime,
  tempResourceId,
  canEditKeyInfo,
  planningResources: keyInfoResources,
  savingKeyInfo,
  startEditingDate,
  startEditingTime,
  startEditingLocation,
  cancelEditingDate,
  cancelEditingTime,
  cancelEditingLocation,
  saveDateTime,
  saveLocation,
} = useEventKeyInfoEditing({
  eventId,
  event,
  canEditEvent,
  accountSpaces,
  onEventUpdated: updated => {
    if (updated) event.value = updated;
  },
});

const eventAccentColor = computed(() => resolveEventAccentColor(event.value));

const openAddTimeslot = () => {
  editingTimeslot.value = null;
  timeslotModalOpen.value = true;
};

const openEditTimeslot = allocation => {
  editingTimeslot.value = allocation;
  timeslotModalOpen.value = true;
};

const handleSaveTimeslot = async form => {
  try {
    await saveTimeslot(form, editingTimeslot.value);
    timeslotModalOpen.value = false;
    editingTimeslot.value = null;
    fetchPricingBreakdown();
  } catch {
    // Keep the modal open so the user can adjust and retry.
  }
};

// ── Computed ──
const displayCurrency = computed(() =>
  accountCurrency(currentAccount.value)
);

const activeBreakdown = computed(
  () => pricingDisplayBreakdown.value || pricingBreakdown.value
);

const acceptedQuote = computed(
  () =>
    quotes.value.find(q => q.status === 'accepted') ||
    event.value?.accepted_quote ||
    null
);

const openIssuedQuoteBounds = computed(() => quotedPriceBounds(quotes.value));

const priceMode = computed(() => {
  if (!canViewPricing.value) return 'suggested';
  if (acceptedQuote.value) return 'accepted';
  if (openIssuedQuoteBounds.value) return 'quoted';
  return 'suggested';
});

const rateQuoteForSuggested = computed(() =>
  suggestedRateQuote(acceptedQuote.value, compareQuote.value)
);

const suggestedPricing = computed(() =>
  computeSuggestedPricing(activeBreakdown.value, rateQuoteForSuggested.value)
);

const suggestedTotal = computed(() => {
  if (!canViewPricing.value) return null;
  if (suggestedPricing.value.total != null) return suggestedPricing.value.total;
  return pricingDisplayTotal.value;
});

const displayPriceTotal = computed(() => {
  if (!canViewPricing.value) return null;
  if (acceptedQuote.value) {
    return Number(acceptedQuote.value.total_excl_tax || 0);
  }
  return suggestedTotal.value;
});

const formatMoneyAmount = (value, currency) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return null;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || displayCurrency.value,
    maximumFractionDigits: 2,
  }).format(Number(value));
};

const pricingCurrency = computed(() => {
  if (acceptedQuote.value?.currency) return acceptedQuote.value.currency;
  const issued = quotes.value.find(q =>
    ['sent', 'viewed'].includes(q.status)
  );
  return (
    issued?.currency ||
    activeBreakdown.value?.currency ||
    displayCurrency.value
  );
});

const formattedTotal = computed(() =>
  formatMoneyAmount(displayPriceTotal.value, pricingCurrency.value)
);

const formattedSidebarTotal = computed(() => {
  if (priceMode.value === 'quoted' && openIssuedQuoteBounds.value) {
    const { min, max } = openIssuedQuoteBounds.value;
    if (min === max) return formatMoneyAmount(min, pricingCurrency.value);
    const low = formatMoneyAmount(min, pricingCurrency.value);
    const high = formatMoneyAmount(max, pricingCurrency.value);
    if (!low || !high) return low || high;
    return `${low} – ${high}`;
  }
  return formattedTotal.value;
});

const formattedSuggested = computed(() => {
  if (!canViewPricing.value || !acceptedQuote.value) return null;
  if (suggestedTotal.value === null) return null;
  const quoted = Number(acceptedQuote.value.total_excl_tax || 0);
  if (Number(suggestedTotal.value) === quoted) return null;
  return formatMoneyAmount(suggestedTotal.value, pricingCurrency.value);
});

const impactDelta = computed(() => {
  if (
    !canViewPricing.value ||
    !acceptedQuote.value ||
    suggestedTotal.value === null
  ) {
    return null;
  }
  return (
    Number(suggestedTotal.value) - Number(acceptedQuote.value.total_excl_tax || 0)
  );
});

const formattedImpact = computed(() => {
  if (impactDelta.value === null || impactDelta.value === 0) return null;
  const prefix = impactDelta.value > 0 ? '+' : '';
  return prefix + formatMoneyAmount(impactDelta.value, pricingCurrency.value);
});

const formattedDelta = computed(() => {
  if (priceDelta.value === null || priceDelta.value === 0) return null;
  const bd = pricingDisplayBreakdown.value || pricingBreakdown.value;
  const prefix = priceDelta.value > 0 ? '+' : '';
  return (
    prefix +
    formatMoneyAmount(priceDelta.value, bd?.currency || displayCurrency.value)
  );
});

const SHOW_ONLY_QUERY_KEYS = [
  'from',
  'fromConversation',
  'fromDate',
  'fromHash',
];

const returnListQuery = computed(() => {
  const query = { ...route.query };
  SHOW_ONLY_QUERY_KEYS.forEach(key => {
    delete query[key];
  });
  return query;
});

const returnRoute = computed(() => {
  if (route.query.from === 'board') {
    return {
      name: 'planning_events_board',
      query: returnListQuery.value,
    };
  }

  if (route.query.from === 'calendar') {
    const calendarRoute = { name: 'planning_calendar' };
    if (route.query.fromDate) {
      calendarRoute.params = { date: route.query.fromDate };
    }
    if (route.query.fromHash) {
      calendarRoute.hash = `#${route.query.fromHash}`;
    }
    return calendarRoute;
  }

  return {
    name: 'planning_events',
    query: returnListQuery.value,
  };
});

const customer = computed(() => {
  if (!event.value?.customer_id) return null;
  return store.getters['contacts/getContact'](event.value.customer_id);
});

const eventManager = computed(() => {
  if (!event.value?.event_manager_id) return null;
  const agent = store.getters['agents/getAgentById'](event.value.event_manager_id);
  const nested = event.value.event_manager;
  if (!agent?.id && !nested) return null;
  return {
    ...nested,
    ...agent,
    phone_number: agent.phone_number || nested?.phone,
  };
});

const formattedAgents = computed(() => {
  if (!showEventManagerDropdown.value) return [];
  return allAgents.value.map(agent => ({
    id: agent.id,
    name: agent.name,
    email: agent.email,
    thumbnail: agent.thumbnail,
    availability_status: agent.availability_status,
  }));
});

const selectedEventManager = computed(() => {
  if (!eventManager.value) return {};
  return {
    id: eventManager.value.id,
    name: eventManager.value.name,
    thumbnail: eventManager.value.thumbnail,
    availability_status: eventManager.value.availability_status,
  };
});

const formattedContacts = computed(() => {
  if (!showCustomerDropdown.value) return [];
  return allContacts.value.map(contact => ({
    id: contact.id,
    name: contact.name,
    email: contact.email,
    thumbnail: contact.thumbnail,
  }));
});

const selectedCustomer = computed(() => {
  if (!customer.value) return {};
  return {
    id: customer.value.id,
    name: customer.value.name,
    thumbnail: customer.value.thumbnail,
  };
});

const attributesByCategory = computed(() => {
  return store.getters['attributes/getEventAttributesByCategory'] || {};
});

const EMPTY_HIDDEN_SECTION_KEYS = Object.freeze([]);
const hiddenSectionKeys = computed(
  () =>
    event.value?.metadata?.hidden_section_keys || EMPTY_HIDDEN_SECTION_KEYS
);

const sectionHiddenKeys = computed(() => {
  const keys = [...(hiddenSectionKeys.value || [])];
  if (!quotesEnabled.value && !keys.includes('quotes')) {
    keys.push('quotes');
  }
  if (!documentsEnabled.value && !keys.includes('documents')) {
    keys.push('documents');
  }
  if (!canViewPricing.value) {
    if (!keys.includes('pricing')) keys.push('pricing');
    if (!keys.includes('quotes')) keys.push('quotes');
  }
  return keys;
});

const FIXED_SECTION_ADD_META = {
  general_info: {
    labelKey: 'PLANNING.SHOW.GENERAL_INFORMATION',
    icon: 'i-lucide-file-text',
  },
  pricing: {
    labelKey: 'PLANNING.SHOW.PRICING_TITLE',
    icon: 'i-lucide-receipt',
  },
  quotes: {
    labelKey: 'PLANNING.SHOW.QUOTES_TITLE',
    icon: 'i-lucide-file-text',
  },
  documents: {
    labelKey: 'PLANNING.SHOW.DOCUMENTS_TITLE',
    icon: 'i-lucide-files',
  },
  floorplans: {
    labelKey: 'PLANNING.EVENT_FLOORPLAN.TITLE',
    icon: 'i-lucide-layout-dashboard',
  },
  schedule: {
    labelKey: 'PLANNING.SHOW.SCHEDULE',
    icon: 'i-lucide-calendar',
  },
};

const {
  visibleCustomAttrSectionKeys,
  availableCategoriesToAdd,
  canManageBlocks,
  isCategoryReadOnly,
  getCategoryLabel,
  getCategoryIcon,
  getAttributesForCategory,
  addBlock,
  onBlockRemoved,
} = useEventCustomAttributeSections({
  event,
  attributesByCategory,
  editableCategoryKeys,
  readOnly: isReadOnly,
  hiddenSectionKeys,
});

const availableFixedSectionsToAdd = computed(() => {
  if (!canEditEvent.value) return [];
  const hidden = new Set(sectionHiddenKeys.value);
  return MAIN_COLUMN_FIXED_SECTION_KEYS.filter(key => {
    if (key === 'quotes' && !quotesEnabled.value) return false;
    if (key === 'documents' && !documentsEnabled.value) return false;
    if (
      (key === 'pricing' || key === 'quotes') &&
      !canViewPricing.value
    ) {
      return false;
    }
    return hidden.has(key);
  }).map(key => ({
    key,
    label: t(FIXED_SECTION_ADD_META[key].labelKey),
    icon: FIXED_SECTION_ADD_META[key].icon,
  }));
});

const availableSectionsToAdd = computed(() => [
  ...availableFixedSectionsToAdd.value,
  ...availableCategoriesToAdd.value,
]);

const canAddSections = computed(
  () => canManageBlocks.value || availableFixedSectionsToAdd.value.length > 0
);

const isPricingSectionVisible = computed(
  () =>
    canViewPricing.value && !hiddenSectionKeys.value.includes('pricing')
);

const pricingShowsQuotes = computed(
  () => isPricingSectionVisible.value && quotes.value.length > 0
);

const handleSelectQuote = id => {
  selectedQuoteId.value = id;
  if (pricingShowsQuotes.value) setCompareQuote(id);
};

const locationNames = computed(() => {
  const seen = new Set();
  const names = [];

  childAllocations.value.forEach(allocation => {
    if (!allocation.resource_id || seen.has(allocation.resource_id)) return;
    seen.add(allocation.resource_id);

    const name = getResourceName(allocation);
    if (name && name !== 'Unassigned' && name !== 'Unknown') {
      names.push(name);
    }
  });

  if (names.length) return names;

  (event.value?.allocated_resources || []).forEach(resource => {
    if (!resource.resource_id || seen.has(resource.resource_id)) return;
    seen.add(resource.resource_id);
    if (resource.name) names.push(resource.name);
  });

  return names;
});

const locationDisplay = computed(() => locationNames.value.join(', '));

const canEditKeyInfoLocation = computed(
  () => canEditKeyInfo.value && locationNames.value.length <= 1
);

const dateDisplay = computed(() => {
  const startDate = allocationDateRange.value?.start || null;
  const endDate = allocationDateRange.value?.end || null;

  if (!startDate || Number.isNaN(startDate.getTime())) return 'TBD';

  const startFormatted = startDate.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  if (!endDate || startDate.toDateString() === endDate.toDateString()) {
    return startFormatted;
  }

  const endDay = endDate.getDate();
  const endMonth = endDate.toLocaleDateString('en-US', { month: 'long' });
  const startMonth = startDate.toLocaleDateString('en-US', { month: 'long' });
  const year = endDate.getFullYear();

  if (startMonth === endMonth) {
    return `${startDate.getDate()}-${endDay} ${endMonth}, ${year}`;
  }
  const startMonthShort = startDate.toLocaleDateString('en-US', {
    month: 'short',
  });
  const endMonthShort = endDate.toLocaleDateString('en-US', { month: 'short' });
  return `${startMonthShort} ${startDate.getDate()} - ${endMonthShort} ${endDay}, ${year}`;
});

const startTime = computed(() => {
  const range = allocationDateRange.value;
  if (!range?.start || Number.isNaN(range.start.getTime())) return 'TBA';
  return formatTime(range.start);
});

const endTime = computed(() => {
  const range = allocationDateRange.value;
  if (!range?.end || Number.isNaN(range.end.getTime())) return 'TBA';
  return formatTime(range.end);
});

const pax = computed(() => {
  const allocationPaxValues = childAllocations.value
    .map(allocation => Number(allocation.metadata?.pax))
    .filter(value => Number.isFinite(value));

  if (allocationPaxValues.length > 0) {
    return Math.max(...allocationPaxValues);
  }

  return (
    event.value?.pax ||
    event.value?.custom_attributes?.pax ||
    event.value?.metadata?.pax
  );
});

const { copyAsMarkdown } = useEventCopyMarkdown({
  event,
  customer,
  eventManager,
  dateDisplay,
  startTime,
  endTime,
  locationDisplay,
  pax,
  formattedTotal,
  pricingLocked: computed(() => planLocked.value || pricingIsLocked.value),
  activeBreakdown,
  childAllocations,
  getResourceName,
  formatAllocationTime,
  attributesByCategory,
});

const { isSectionCollapsed, toggleSection } = useEventSectionCollapse(
  visibleCustomAttrSectionKeys
);

const { orderedSectionKeys, saveSectionOrder } = useEventSectionOrder({
  visibleCustomAttrSectionKeys,
  hiddenSectionKeys: sectionHiddenKeys,
});

const eventSectionItems = ref([]);

watch(
  orderedSectionKeys,
  keys => {
    const nextKeys = keys.filter(key => key !== 'key_info');
    const currentKeys = eventSectionItems.value.map(item => item.key);
    if (
      currentKeys.length === nextKeys.length &&
      currentKeys.every((key, index) => key === nextKeys[index])
    ) {
      return;
    }
    const reconciled = reconcileSectionOrder(currentKeys, keys);
    // `key_info` is rendered in the sidebar, not in the draggable main column.
    eventSectionItems.value = reconciled
      .filter(key => key !== 'key_info')
      .map(key => ({ key }));
  },
  { immediate: true }
);

const onSectionDragEnd = () => {
  saveSectionOrder(eventSectionItems.value.map(item => item.key));
};

const customAttrCategoryForSection = key => categoryFromSectionKey(key);

const PRINT_HIDDEN_SECTION_KEYS = [];

const printSectionKeys = computed(() =>
  eventSectionItems.value
    .map(item => item.key)
    .filter(key => !PRINT_HIDDEN_SECTION_KEYS.includes(key))
);

const schedulePrintIndex = computed(() =>
  printSectionKeys.value.indexOf('schedule')
);

const hasPrintableSectionsBeforeSchedule = computed(
  () => schedulePrintIndex.value > 0
);

const hasPrintableSectionsAfterSchedule = computed(() => {
  const index = schedulePrintIndex.value;
  return index >= 0 && index < printSectionKeys.value.length - 1;
});

// ── Watchers ──

// Sync real-time (ActionCable) updates from the Vuex store into the local event ref
const storeEvent = computed(() =>
  store.getters['planningEvents/getEventById'](eventId.value)
);
const QUOTE_SHEET_REFRESH_DECISIONS = ['accepted', 'rejected'];
let quoteSheetRefreshId = 0;

const refreshSheetForQuoteDecision = async () => {
  const requestId = quoteSheetRefreshId + 1;
  quoteSheetRefreshId = requestId;
  const currentId = eventId.value;
  if (!currentId) return;

  try {
    const refreshed = await store.dispatch('planningEvents/show', currentId);
    if (
      requestId !== quoteSheetRefreshId ||
      String(eventId.value) !== String(currentId)
    ) {
      return;
    }
    if (
      refreshed &&
      (!event.value?.id || String(event.value.id) === String(refreshed.id))
    ) {
      event.value = {
        ...refreshed,
        description: descriptionForOpenEvent(refreshed),
      };
    }
    if (quotesEnabled.value) await refreshQuotes();
    if (
      requestId !== quoteSheetRefreshId ||
      !canViewPricing.value ||
      !event.value
    ) {
      return;
    }
    await loadFromEvent(event.value);
    await fetchPricingBreakdown();
  } catch {
    // Keep the sheet that is already open.
  }
};

const onQuoteDecision = data => {
  if (!data?.id || String(data.id) !== String(eventId.value)) return;
  if (!QUOTE_SHEET_REFRESH_DECISIONS.includes(data.quote_decision)) return;
  refreshSheetForQuoteDecision();
};

useEmitter(BUS_EVENTS.PLANNING_EVENT_UPDATED, onQuoteDecision);

watch(storeEvent, (newVal, oldVal) => {
  const currentId = eventId.value && String(eventId.value);
  const openId = event.value?.id && String(event.value.id);
  // Ignore store updates until the sheet is actually showing this event.
  // On navigation the route id changes before fetch returns, and the store
  // already holds the next event (list or socket). Merging that onto the
  // previous sheet keeps the old description under the new name.
  if (
    newVal &&
    event.value &&
    openId &&
    openId === currentId &&
    String(newVal.id) === currentId &&
    !isOlderPlanningEvent(newVal, event.value)
  ) {
    // Merge socket/API store updates — never replace the whole sheet object
    // unless sheet fields actually changed (avoids quotes/sidebar remounts).
    const next = applyPlanningEventPatch(event.value, newVal);
    if (next !== event.value) {
      event.value = {
        ...next,
        description: descriptionForOpenEvent(next),
      };
      initSharingFromEvent(next);
    }
  } else if (
    !newVal &&
    oldVal &&
    currentId &&
    String(oldVal.id) === currentId
  ) {
    // Event was deleted by another user — redirect to list (only when not embedded in a panel)
    if (!props.eventId) router.push(returnRoute.value);
  }
});

watch(
  event,
  newVal => {
    if (newVal) {
      initSharingFromEvent(newVal);
    }
  },
  { immediate: true }
);

// Initialise editor content once when the event first loads
watch(
  () => event.value?.id,
  (newId, oldId) => {
    if (newId && newId !== oldId) {
      generalInfoContent.value = event.value?.description || '';
      generalInfoEventId.value = newId;
    }
  },
  { immediate: true }
);

const slotPricingSignature = allocation => {
  const metadata = allocation?.metadata || {};
  const attrs = metadata.custom_attributes || {};
  const attrSig = Object.keys(attrs)
    .sort()
    .map(key => `${key}=${JSON.stringify(attrs[key])}`)
    .join(',');
  return [
    allocation.id,
    allocation.start_at,
    allocation.end_at,
    allocation.resource_id,
    metadata.pax ?? '',
    attrSig,
  ].join(':');
};

const allocationPricingFingerprint = computed(() =>
  childAllocations.value.map(slotPricingSignature).join('|')
);

watch(allocationPricingFingerprint, (newFingerprint, oldFingerprint) => {
  if (!canViewPricing.value || !oldFingerprint || loading.value) return;
  fetchPricingBreakdown();
});

watch(
  () => event.value?.metadata?.locked_pricing?.locked_at,
  () => {
    if (
      !canViewPricing.value ||
      !event.value ||
      quotesEnabled.value ||
      loading.value
    ) {
      return;
    }
    loadFromEvent(event.value).then(() => fetchPricingBreakdown());
  }
);

// ── Lifecycle ──
const fetchEvent = async () => {
  const requestedId = eventId.value;
  if (!requestedId || requestedId === 'undefined') return null;
  try {
    const fetched = await store.dispatch('planningEvents/show', requestedId);
    if (!fetched || String(fetched.id) !== String(eventId.value)) return null;
    if (isOlderPlanningEvent(fetched, event.value)) return event.value;
    const previousId = event.value?.id;
    event.value = fetched;
    if (!previousId || String(previousId) !== String(fetched.id)) {
      generalInfoContent.value = fetched.description || '';
      generalInfoEventId.value = fetched.id;
    }
    return fetched;
  } catch {
    if (String(eventId.value) !== String(requestedId)) return null;
    if (!props.eventId) router.push({ name: 'planning_events' });
    return null;
  }
};

const dispatchIfEmpty = (getter, action) => {
  const value = store.getters[getter];
  if (Array.isArray(value) && value.length) return Promise.resolve();
  return store.dispatch(action).catch(() => undefined);
};

const loadSecondaryEventData = async () => {
  const loadPricing = async () => {
    if (!canViewPricing.value) return;
    await loadPricingPlans();
    await loadFromEvent(event.value);
    await fetchPricingBreakdown();
  };

  await Promise.all([
    fetchEventFloorplans(),
    loadPricing(),
    canViewPricing.value && quotesEnabled.value
      ? fetchQuotes()
      : Promise.resolve(),
    documentsEnabled.value ? fetchDocuments() : Promise.resolve(),
    documentsEnabled.value ? fetchDocumentLibrary() : Promise.resolve(),
  ]);
};

const ensureCustomerLoaded = async () => {
  if (
    event.value?.customer_id &&
    !store.getters['contacts/getContact'](event.value.customer_id)?.id
  ) {
    await store.dispatch('contacts/show', { id: event.value.customer_id });
  }
};

const stillOnEvent = requestedId =>
  String(eventId.value) === String(requestedId);

const loadEventPage = async () => {
  const requestedId = eventId.value;
  loading.value = true;
  resetPricing();
  resetQuotes();
  const fetched = await fetchEvent();
  if (!stillOnEvent(requestedId)) return;

  if (!fetched) {
    loading.value = false;
    return;
  }

  await ensureCustomerLoaded();
  if (!stillOnEvent(requestedId)) return;
  loading.value = false;
  await loadSecondaryEventData();
};

onMounted(async () => {
  loading.value = true;
  const requestedId = eventId.value;
  await fetchEvent();
  if (!stillOnEvent(requestedId)) return;

  if (!event.value) {
    loading.value = false;
    return;
  }

  try {
    await Promise.all([
      dispatchIfEmpty('agents/getAgents', 'agents/get'),
      dispatchIfEmpty('attributes/getAttributes', 'attributes/get'),
      dispatchIfEmpty(
        'customAttributeCategories/getRecords',
        'customAttributeCategories/get'
      ),
      dispatchIfEmpty('planningResources/getResources', 'planningResources/get'),
      dispatchIfEmpty('spaces/getSpaces', 'spaces/get'),
      dispatchIfEmpty(
        'planningEventPortals/getRecords',
        'planningEventPortals/get'
      ),
      dispatchIfEmpty('inboxes/getInboxes', 'inboxes/get'),
      dispatchIfEmpty('dataHub/getConnections', 'dataHub/fetchConnections'),
      dispatchIfEmpty(
        'planningEventLabels/getRecords',
        'planningEventLabels/get'
      ),
      store
        .dispatch('planningEvents/getStatusOptions')
        .then(response => {
          statusOptions.value = buildOrderedStatusOptions(response);
        })
        .catch(() => undefined),
      ensureCustomerLoaded().catch(() => undefined),
    ]);
  } finally {
    if (stillOnEvent(requestedId)) loading.value = false;
  }

  if (!stillOnEvent(requestedId)) return;

  if (isCloudFeatureEnabled(FEATURE_FLAGS.LEAD_NETWORKS)) {
    LeadNetworkAPI.getMemberships()
      .then(({ data }) => {
        leadNetworkMemberships.value = data || [];
      })
      .catch(() => {
        leadNetworkMemberships.value = [];
      });
  }

  loadSecondaryEventData();

  if (!eventId.value || eventId.value === 'undefined') return;

  store.dispatch('planningEventViewingStatus/toggleViewing', {
    eventId: eventId.value,
    status: 'on',
  });

  if (event.value?.unread) {
    readTimer = setTimeout(async () => {
      try {
        await store.dispatch('planningEvents/markRead', eventId.value);
        if (event.value) event.value.unread = false;
      } catch {
        // Ignore mark-read failures; user can reopen to retry
      }
    }, 3000);
  }
});

watch(eventId, async (id, prevId) => {
  if (id === prevId) return;
  if (prevId) persistDescription.flush?.();
  if (!id || id === 'undefined') return;

  timeslotModalOpen.value = false;
  if (readTimer) {
    clearTimeout(readTimer);
    readTimer = null;
  }
  if (prevId) {
    store.dispatch('planningEventViewingStatus/toggleViewing', {
      eventId: prevId,
      status: 'off',
    });
  }

  await loadEventPage();

  store.dispatch('planningEventViewingStatus/toggleViewing', {
    eventId: id,
    status: 'on',
  });
});

// Heartbeat: re-send viewing_on every 50s so presence stays active
const viewingHeartbeat = setInterval(() => {
  if (eventId.value) {
    store.dispatch('planningEventViewingStatus/toggleViewing', {
      eventId: eventId.value,
      status: 'on',
    });
  }
}, 50000);

onBeforeUnmount(() => {
  persistDescription.flush?.();
  if (readTimer) {
    clearTimeout(readTimer);
    readTimer = null;
  }
  clearInterval(viewingHeartbeat);
  if (!eventId.value || eventId.value === 'undefined') return;

  store.dispatch('planningEventViewingStatus/toggleViewing', {
    eventId: eventId.value,
    status: 'off',
  });
});

// ── Actions ──
const editAllocations = () => {
  const query = { selectEventId: eventId.value };
  const dateForCalendar = allocationDateRange.value?.start;
  const params = {};
  if (dateForCalendar && !Number.isNaN(new Date(dateForCalendar).getTime())) {
    params.date = formatDateParam(dateForCalendar);
  }
  router.push({ name: 'planning_calendar', params, query, hash: '#day' });
};

const handleDelete = () => deleteDialogRef.value?.open();

const openRescheduleModal = () => {
  showRescheduleModal.value = true;
};

const handleReschedule = async ({ startDate }) => {
  if (!event.value?.id || !startDate) return;

  rescheduleSaving.value = true;
  try {
    const updated = await store.dispatch('planningEvents/reschedule', {
      id: event.value.id,
      startDate,
    });
    event.value = updated;
    showRescheduleModal.value = false;
    useAlert(t('PLANNING.SHOW.RESCHEDULE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('PLANNING.SHOW.RESCHEDULE_ERROR')
    );
  } finally {
    rescheduleSaving.value = false;
  }
};

const openDuplicateModal = () => {
  showDuplicateModal.value = true;
};

const handleDuplicate = async ({ startDate }) => {
  if (!event.value?.id || !startDate) return;

  duplicateSaving.value = true;
  try {
    const created = await store.dispatch('planningEvents/duplicate', {
      id: event.value.id,
      startDate,
    });
    showDuplicateModal.value = false;
    useAlert(t('PLANNING.SHOW.DUPLICATE_SUCCESS'));
    router.push({
      name: 'planning_events_show',
      params: { eventId: created.id },
      query: { ...route.query },
    });
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('PLANNING.SHOW.DUPLICATE_ERROR')
    );
  } finally {
    duplicateSaving.value = false;
  }
};

const handlePrint = () => window.print();

const handleMarkRead = async () => {
  if (!eventId.value || !event.value?.unread) return;
  try {
    await store.dispatch('planningEvents/markRead', eventId.value);
    if (event.value) event.value.unread = false;
  } catch {
    // Ignore mark-read failures
  }
};

const handleMarkUnread = async () => {
  if (!eventId.value || event.value?.unread) return;
  try {
    await store.dispatch('planningEvents/markUnread', eventId.value);
    if (event.value) event.value.unread = true;
  } catch {
    // Ignore mark-unread failures
  }
};

const confirmDelete = async () => {
  try {
    await store.dispatch('planningEvents/delete', eventId.value);
    deleteDialogRef.value?.close();
    if (route.name?.startsWith('event_inbox')) {
      router.push({
        name: 'event_inbox',
        params: { accountId: accountId.value },
      });
    } else if (!props.eventId) {
      router.push(returnRoute.value);
    }
  } catch {
    deleteDialogRef.value?.close();
  }
};

const startEditingName = () => {
  tempName.value = event.value?.name || '';
  editingName.value = true;
};

const saveEventName = async () => {
  if (!tempName.value.trim()) return;
  try {
    saving.value = true;
    const updatedEvent = await store.dispatch('planningEvents/update', {
      id: eventId.value,
      name: tempName.value,
    });
    if (updatedEvent) event.value = updatedEvent;
    editingName.value = false;
  } finally {
    saving.value = false;
  }
};

const cancelEditingName = () => {
  editingName.value = false;
  tempName.value = '';
};

const updateStage = async statusPayload => {
  try {
    saving.value = true;
    const updatedEvent = await updateEventStage(event.value, statusPayload);
    if (updatedEvent) {
      event.value = updatedEvent;
      if (statusPayload.system_stage === 'confirmed') triggerConfetti();
    }
    if (!pricingDisplayTotal.value) fetchPricingBreakdown();
  } finally {
    saving.value = false;
  }
};

const updateEventLabel = async eventLabelId => {
  try {
    saving.value = true;
    const updatedEvent = await store.dispatch('planningEvents/update', {
      id: eventId.value,
      event_label_id: eventLabelId,
    });
    if (updatedEvent) event.value = updatedEvent;
    fetchPricingBreakdown();
  } finally {
    saving.value = false;
  }
};

const buildColorMetadata = color => {
  const metadata = { ...(event.value?.metadata || {}) };
  delete metadata.color;
  if (color) {
    metadata.border_color = color.toLowerCase();
    return metadata;
  }
  delete metadata.border_color;
  if (Object.keys(metadata).length) return metadata;
  return { border_color: null };
};

const persistEventColor = async color => {
  try {
    saving.value = true;
    const updatedEvent = await store.dispatch('planningEvents/update', {
      id: eventId.value,
      metadata: buildColorMetadata(color),
    });
    if (updatedEvent) event.value = updatedEvent;
  } finally {
    saving.value = false;
  }
};

const debouncedPersistEventColor = useDebounceFn(persistEventColor, 300);

onBeforeUnmount(() => {
  debouncedPersistEventColor.flush?.();
});

const updateEventColor = color => {
  if (color == null || CUSTOM_COLOR_SWATCHES.includes(color)) {
    debouncedPersistEventColor.cancel?.();
    return persistEventColor(color);
  }
  return debouncedPersistEventColor(color);
};

const restoreSheetScroll = top => {
  const el = sheetScrollRef.value;
  if (!el || top == null) return;
  el.scrollTop = top;
  requestAnimationFrame(() => {
    if (sheetScrollRef.value) sheetScrollRef.value.scrollTop = top;
  });
};

const closeEventManagerDropdown = () => {
  const top = sheetScrollRef.value?.scrollTop ?? 0;
  showEventManagerDropdown.value = false;
  nextTick(() => restoreSheetScroll(top));
};

const updateEventManager = async agent => {
  if (!canManageEvent.value) return;
  const top = sheetScrollRef.value?.scrollTop ?? 0;
  try {
    saving.value = true;
    const updatedEvent = await store.dispatch('planningEvents/update', {
      id: eventId.value,
      event_manager_id: agent?.id ?? null,
    });
    if (updatedEvent && event.value) {
      const next = applyPlanningEventPatch(event.value, updatedEvent);
      if (next !== event.value) {
        event.value = {
          ...next,
          description: descriptionForOpenEvent(next),
        };
      }
    }
    showEventManagerDropdown.value = false;
  } finally {
    saving.value = false;
    nextTick(() => restoreSheetScroll(top));
  }
};

const updateCustomer = async contact => {
  if (!contact || !contact.id) return;
  try {
    saving.value = true;
    const updatedEvent = await store.dispatch('planningEvents/update', {
      id: eventId.value,
      customer_id: contact.id,
    });
    if (updatedEvent) event.value = updatedEvent;
    showCustomerDropdown.value = false;
  } finally {
    saving.value = false;
  }
};

const updateCustomAttribute = async (attributeKey, value) => {
  try {
    const updatedEvent = await store.dispatch('planningEvents/update', {
      id: eventId.value,
      custom_attributes: {
        ...(event.value?.custom_attributes || {}),
        [attributeKey]: value,
      },
    });
    if (updatedEvent) event.value = updatedEvent;
    fetchPricingBreakdown();
  } catch {
    // Error handled by store
  }
};

const deleteCustomAttribute = async attributeKey => {
  try {
    const newCustomAttributes = { ...(event.value?.custom_attributes || {}) };
    delete newCustomAttributes[attributeKey];
    const updatedEvent = await store.dispatch('planningEvents/update', {
      id: eventId.value,
      custom_attributes: newCustomAttributes,
    });
    if (updatedEvent) event.value = updatedEvent;
    fetchPricingBreakdown();
  } catch {
    // Error handled by store
  }
};

const copyCustomAttribute = async value => {
  try {
    await navigator.clipboard.writeText(value);
  } catch (error) {
    console.error('Failed to copy:', error);
  }
};

const updateHiddenSectionKeys = async keys => {
  const metadata = {
    ...(event.value?.metadata || {}),
    hidden_section_keys: keys,
  };
  const updatedEvent = await store.dispatch('planningEvents/update', {
    id: eventId.value,
    metadata,
  });
  if (updatedEvent) event.value = updatedEvent;
};

const hideSection = async key => {
  const current = event.value?.metadata?.hidden_section_keys || [];
  if (current.includes(key)) return;
  try {
    await updateHiddenSectionKeys([...current, key]);
    if (isCustomAttrSectionKey(key)) {
      onBlockRemoved(categoryFromSectionKey(key));
    }
  } catch {
    // Error handled by store
  }
};

const showSection = async key => {
  const current = event.value?.metadata?.hidden_section_keys || [];
  try {
    if (current.includes(key)) {
      await updateHiddenSectionKeys(current.filter(k => k !== key));
    }
    if (isCustomAttrSectionKey(key)) {
      addBlock(categoryFromSectionKey(key));
    }
  } catch {
    // Error handled by store
  }
};

const removeBlock = async category => {
  await hideSection(customAttrSectionKey(category));
};

const handleAddSection = async key => {
  await showSection(key);
};

const removeSectionMenuAction = computed(() => ({
  key: 'remove-block',
  label: t('PLANNING.SHOW.REMOVE_BLOCK'),
  icon: 'i-lucide-trash-2',
  danger: true,
}));

const handleContactSearch = async query => {
  if (!query || query.trim().length === 0) {
    await store.dispatch('contacts/get', { page: 1 });
    return;
  }
  try {
    searchingContacts.value = true;
    await store.dispatch('contacts/search', {
      search: query,
      page: 1,
      sortAttr: 'name',
    });
  } finally {
    searchingContacts.value = false;
  }
};

const handleAgentSearch = () => {
  searchingAgents.value = false;
};

const openCustomerDropdown = () => {
  if (!canEditEvent.value) return;
  showCustomerDropdown.value = true;
  showEventManagerDropdown.value = false;
  if (!allContacts.value.length) {
    store.dispatch('contacts/get', { page: 1 });
  }
};

const openEventManagerDropdown = () => {
  if (!canManageEvent.value) return;
  showEventManagerDropdown.value = true;
  showCustomerDropdown.value = false;
};

const handleDeleteAllocation = async allocation => {
  const hasChildren = allocation?.sub_allocations?.length > 0;

  try {
    deletingAllocationId.value = allocation.id;
    await store.dispatch('planningAllocations/delete', {
      id: allocation.id,
      cascade: hasChildren,
    });
    await fetchEvent();
  } finally {
    deletingAllocationId.value = null;
  }
};

const requestDeleteAllocation = allocation => {
  pendingDeleteAllocation.value = allocation;
  deleteSlotDialogRef.value?.open();
};

const pendingDeleteQuotedQuotes = computed(() =>
  openQuotesForAllocation(quotes.value, pendingDeleteAllocation.value?.id)
);

const pendingDeleteQuotedWarning = computed(() => {
  const matched = pendingDeleteQuotedQuotes.value;
  if (!matched.length) return null;
  if (matched.length === 1) {
    return t('PLANNING.SHOW.QUOTED_SLOT_DELETE_WARNING', {
      quote: quoteDisplayLabel(matched[0], t),
    });
  }
  return t('PLANNING.SHOW.QUOTED_SLOT_DELETE_WARNING_MANY', {
    count: matched.length,
  });
});

const confirmDeleteAllocation = async () => {
  if (!pendingDeleteAllocation.value) return;
  const allocation = pendingDeleteAllocation.value;
  pendingDeleteAllocation.value = null;
  deleteSlotDialogRef.value?.close();
  await handleDeleteAllocation(allocation);
};
</script>

<template>
  <div
    v-if="!loading && event"
    class="event-sheet-print flex flex-col h-full bg-surface-1 overflow-hidden print:overflow-visible print:h-auto print:bg-white"
    @dragenter="onDocumentDragEnter"
    @dragover="onDocumentDragOver"
    @dragleave="onDocumentDragLeave"
    @drop="onDocumentDrop"
  >
    <DocumentDropOverlay
      :is-drag-over="documentDropActive"
      :title="t('PLANNING.SHOW.DOCUMENTS_DROP_HINT')"
      :subtitle="t('PLANNING.SHOW.DOCUMENTS_DROP_SUBTITLE')"
    />
    <Transition
      enter-active-class="transition-opacity duration-200"
      leave-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      leave-from-class="opacity-100"
    >
      <div
        v-if="documentUploading"
        class="fixed inset-0 z-50 backdrop-blur-sm flex items-center justify-center print:hidden"
      >
        <div
          class="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-8 text-center"
        >
          <div
            class="w-8 h-8 border-4 border-woot-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"
          />
          <p class="text-sm font-medium text-n-slate-12">
            {{ t('PLANNING.SHOW.DOCUMENTS_UPLOADING') }}
          </p>
        </div>
      </div>
    </Transition>
    <!-- Top Header -->
    <EventShowHeader
      :event="event"
      :editing-name="editingName"
      :temp-name="tempName"
      :saving="saving"
      :status-options="statusOptions"
      :event-labels="eventLabels"
      :viewers="viewers"
      :read-only="isReadOnly"
      :can-delete="canDeleteEvent"
      :can-change-stage="canChangeStage"
      :can-sell-to-network="canSellToNetwork"
      @start-edit-name="startEditingName"
      @save-name="saveEventName"
      @cancel-edit-name="cancelEditingName"
      @update:temp-name="tempName = $event"
      @open-history="openHistoryDialog"
      @open-share="shareDialogRef?.open()"
      @copy-markdown="copyAsMarkdown"
      @print="handlePrint"
      @delete="handleDelete"
      @edit-allocations="editAllocations"
      @reschedule="openRescheduleModal"
      @duplicate="openDuplicateModal"
      @sell-to-network="showSellToNetworkModal = true"
      @mark-read="handleMarkRead"
      @mark-unread="handleMarkUnread"
      @update-stage="updateStage"
      @update-event-label="updateEventLabel"
      @update-color="updateEventColor"
    />

    <SellToNetworkModal
      v-if="showSellToNetworkModal"
      :event="event"
      :networks="leadNetworkMemberships"
      @close="showSellToNetworkModal = false"
      @sold="fetchEvent"
    />

    <div class="hidden print:block px-8 py-4 border-b border-slate-300">
      <h1 class="text-2xl font-bold text-slate-900">
        {{ event.name }}
      </h1>
    </div>

    <!-- Main Content Grid -->
    <div class="flex-1 overflow-hidden print:overflow-visible">
      <div
        ref="sheetScrollRef"
        class="h-full overflow-y-auto overscroll-y-contain [overflow-anchor:none] px-8 bg-n-surface-1 print:h-auto print:overflow-visible print:p-4 print:bg-white"
      >
        <div
          class="max-w-7xl mx-auto grid grid-cols-1 xl:grid-cols-4 gap-12 print:max-w-none print:block print:gap-6"
        >
          <!-- Left Column (Main Info) -->
          <div class="xl:col-span-3 flex min-w-0 flex-col print:py-1">
            <Draggable
              v-model="eventSectionItems"
              item-key="key"
              handle=".drag-handle"
              animation="200"
              ghost-class="opacity-50"
              class="-ml-8 min-w-0 pl-8 divide-y divide-n-slate-5 [&>*]:py-4 print:ml-0 print:pl-0"
              @end="onSectionDragEnd"
            >
              <template #item="{ element }">
                <div
                  class="group/section relative min-w-0 print:break-inside-avoid bg-n-surface-1"
                  :class="{
                    'print:hidden': PRINT_HIDDEN_SECTION_KEYS.includes(
                      element.key
                    ),
                    'print:break-before-page':
                      element.key === 'schedule' &&
                      hasPrintableSectionsBeforeSchedule,
                    'print:break-after-page':
                      element.key === 'schedule' &&
                      hasPrintableSectionsAfterSchedule,
                    'event-sheet-print-schedule': element.key === 'schedule',
                  }"
                >
                  <SectionCard
                    v-if="element.key === 'general_info'"
                    :title="t('PLANNING.SHOW.GENERAL_INFORMATION')"
                    icon="i-lucide-file-text"
                    collapsible
                    collapse-in-menu
                    :collapse-label="t('PLANNING.SHOW.COLLAPSE')"
                    :expand-label="t('PLANNING.SHOW.EXPAND')"
                    :menu-actions="
                      canEditEvent ? [removeSectionMenuAction] : []
                    "
                    flush
                    :collapsed="isSectionCollapsed('general_info').value"
                    @toggle-collapse="toggleSection('general_info')"
                    @menu-action="
                      action =>
                        action === 'remove-block' && hideSection('general_info')
                    "
                  >
                    <div
                      v-if="event?.experience"
                      class="flex items-center gap-1.5 mb-3 text-xs text-n-slate-10"
                    >
                      <span class="i-lucide-layers size-3.5 shrink-0" />
                      <span>{{ t('PLANNING.SHOW.EXPERIENCE_LABEL') }}:</span>
                      <router-link
                        v-if="isAdmin"
                        :to="{
                          name: 'planning_experiences_edit',
                          params: {
                            accountId: event.account_id,
                            experienceId: event.experience.id,
                          },
                        }"
                        class="font-medium text-n-brand hover:underline print:hidden"
                      >
                        {{ event.experience.name }}
                      </router-link>
                      <span
                        class="font-medium text-slate-700"
                        :class="{ 'hidden print:inline': isAdmin }"
                      >
                        {{ event.experience.name }}
                      </span>
                    </div>
                    <EventGeneralInfo
                      v-model="generalInfoContent"
                      :editable="canEditEvent"
                      @update:model-value="saveDescription"
                    />
                  </SectionCard>

                  <EventCustomAttributeBlock
                    v-else-if="isCustomAttrSectionKey(element.key)"
                    :category="customAttrCategoryForSection(element.key)"
                    :event="event"
                    :attributes="
                      getAttributesForCategory(
                        customAttrCategoryForSection(element.key)
                      )
                    "
                    :title="
                      getCategoryLabel(
                        customAttrCategoryForSection(element.key)
                      )
                    "
                    :icon="
                      getCategoryIcon(customAttrCategoryForSection(element.key))
                    "
                    :read-only="
                      isCategoryReadOnly(
                        customAttrCategoryForSection(element.key)
                      )
                    "
                    :collapsed="isSectionCollapsed(element.key).value"
                    :show-remove-block="
                      !isCategoryReadOnly(
                        customAttrCategoryForSection(element.key)
                      )
                    "
                    @toggle-collapse="toggleSection(element.key)"
                    @update-attribute="updateCustomAttribute"
                    @delete-attribute="deleteCustomAttribute"
                    @copy-attribute="copyCustomAttribute"
                    @remove-block="removeBlock"
                  />

                  <EventPricingSection
                    v-else-if="element.key === 'pricing'"
                    :selected-plan="selectedPlan"
                    :plans="pricingPlans"
                    :selected-plan-id="selectedPlanId"
                    :active-breakdown="activeBreakdown"
                    :compare-quote="quotesEnabled ? compareQuote : null"
                    :quotes="quotesEnabled ? quotes : []"
                    :plan-locked="
                      quotesEnabled ? planLocked || pricingIsLocked : pricingIsLocked
                    "
                    :quotes-enabled="quotesEnabled"
                    :pricing-locked="pricingIsLocked"
                    :locking-price="lockingPrice"
                    :formatted-total="formattedTotal"
                    :formatted-impact="formattedImpact"
                    :formatted-delta="formattedDelta"
                    :impact-delta="impactDelta"
                    :price-delta="priceDelta"
                    :loading-price="loadingPrice"
                    :read-only="isReadOnly"
                    :show-remove-block="canEditEvent"
                    :collapsed="isSectionCollapsed('pricing').value"
                    @toggle-collapse="toggleSection('pricing')"
                    @switch-plan="switchPlan"
                    @lock-price="lockPrice"
                    @unlock-price="unlockPrice"
                    @compare-quote="
                      id => setCompareQuote(id ? Number(id) || id : null)
                    "
                    @create="canManageQuotes && openBuilder()"
                    @remove-block="hideSection('pricing')"
                  />

                  <EventQuotesSection
                    v-else-if="element.key === 'quotes' && quotesEnabled"
                    :quotes="filteredQuotes"
                    :total-count="quotes.length"
                    :filter="quotesFilter"
                    :selected-quote-id="selectedQuoteId"
                    :compare-quote-id="compareQuoteId"
                    :loading="quotesLoading"
                    :read-only="!canManageQuotes"
                    :show-remove-block="canEditEvent"
                    :collapsed="isSectionCollapsed('quotes').value"
                    @toggle-collapse="toggleSection('quotes')"
                    @update:filter="val => (quotesFilter = val)"
                    @select="handleSelectQuote"
                    @create="openBuilder()"
                    @send="sendQuote"
                    @withdraw="withdrawQuote"
                    @accept="handleAcceptQuote"
                    @reject="(id, reason) => rejectQuote(id, reason)"
                    @edit="id => openBuilder(quotes.find(q => q.id === id))"
                    @revise="id => openBuilder(quotes.find(q => q.id === id))"
                    @archive="handleArchiveQuote"
                    @remove-block="hideSection('quotes')"
                  />

                  <EventDocumentsSection
                    v-else-if="element.key === 'documents' && documentsEnabled"
                    :event-id="eventId"
                    :documents="eventDocuments"
                    :libraries="eventDocumentLibrary?.libraries || []"
                    :library-records="eventDocumentLibrary?.records || []"
                    :selected-library-id="selectedDocumentLibraryId"
                    :library-loading="eventDocumentLibraryLoading"
                    :loading="eventDocumentsLoading"
                    :uploading="documentUploading"
                    :read-only="isReadOnly"
                    :show-remove-block="canEditEvent"
                    :collapsed="isSectionCollapsed('documents').value"
                    @toggle-collapse="toggleSection('documents')"
                    @remove-block="hideSection('documents')"
                    @update:selected-library-id="
                      id => (selectedDocumentLibraryId = id)
                    "
                    @refresh-library="id => fetchDocumentLibrary(id)"
                    @attach="payload => handleAttachDocument(payload).catch(() => {})"
                    @upload="payload => handleAttachDocument(payload).catch(() => {})"
                    @update="
                      async (id, payload) => {
                        try {
                          await updateDocument(id, payload);
                        } catch (e) {
                          useAlert(
                            e?.response?.data?.error ||
                              t('PLANNING.SHOW.DOCUMENTS_UPDATE_ERROR')
                          );
                        }
                      }
                    "
                    @remove="
                      async id => {
                        try {
                          await removeDocument(id);
                        } catch (e) {
                          useAlert(
                            e?.response?.data?.error ||
                              t('PLANNING.SHOW.DOCUMENTS_REMOVE_ERROR')
                          );
                        }
                      }
                    "
                    @void="
                      async id => {
                        try {
                          await voidDocument(id);
                        } catch (e) {
                          useAlert(
                            e?.response?.data?.error ||
                              t('PLANNING.SHOW.DOCUMENTS_VOID_ERROR')
                          );
                        }
                      }
                    "
                    @send="doc => {
                      const idx = eventDocuments.findIndex(d => d.id === doc.id);
                      if (idx >= 0) eventDocuments[idx] = doc;
                    }"
                  />

                  <EventFloorplanSection
                    v-else-if="element.key === 'floorplans'"
                    :event-floorplans="eventFloorplans"
                    :loading-floorplans="loadingFloorplans"
                    :creating-floorplan="creatingFloorplan"
                    :show-floorplan-picker="showFloorplanPicker"
                    :floorplan-picker-step="floorplanPickerStep"
                    :selected-picker-space="selectedPickerSpace"
                    :picker-layouts="pickerLayouts"
                    :loading-picker-layouts="loadingPickerLayouts"
                    :available-spaces-for-floorplan="
                      availableSpacesForFloorplan
                    "
                    :read-only="isReadOnly"
                    :show-remove-block="canEditEvent"
                    :collapsed="isSectionCollapsed('floorplans').value"
                    @toggle-collapse="toggleSection('floorplans')"
                    @open-picker="openFloorplanPicker"
                    @close-picker="closeFloorplanPicker"
                    @select-space="selectPickerSpace"
                    @attach-floorplan="attachFloorplan"
                    @delete-floorplan="deleteEventFloorplan"
                    @open-editor="openEventFloorplanEditor"
                    @back-to-space-step="floorplanPickerStep = 'space'"
                    @remove-block="hideSection('floorplans')"
                  />

                  <template v-else-if="element.key === 'schedule'">
                    <div
                      v-if="hasPrintableSectionsBeforeSchedule"
                      class="hidden print:block mb-4 border-b border-slate-300 pb-3"
                    >
                      <p class="text-sm text-slate-500">
                        {{ event.name }}
                      </p>
                      <h2 class="text-xl font-bold text-slate-900">
                        {{ t('PLANNING.SHOW.SCHEDULE') }}
                      </h2>
                    </div>
                    <EventSchedule
                      :child-allocations="childAllocations"
                      :event-id="event?.id"
                      :adding-slot="addingSlot"
                      :deleting-allocation-id="deletingAllocationId"
                      :is-allocation-disco="isAllocationDisco"
                      :get-resource-name="getResourceName"
                      :format-allocation-time="formatAllocationTime"
                      :accent-color="eventAccentColor"
                      :read-only="isReadOnly"
                      :show-remove-block="canEditEvent"
                      :collapsed="isSectionCollapsed('schedule').value"
                      @toggle-collapse="toggleSection('schedule')"
                      @add-timeslot="openAddTimeslot"
                      @edit-allocation="openEditTimeslot"
                      @delete-allocation="requestDeleteAllocation"
                      @duplicate-allocation="duplicateSlot"
                      @remove-block="hideSection('schedule')"
                    />
                  </template>
                </div>
              </template>
            </Draggable>

            <EventCustomAttributeAddBlock
              v-if="canAddSections && availableSectionsToAdd.length"
              class="border-t border-n-slate-5 py-6"
              :options="availableSectionsToAdd"
              @add="handleAddSection"
            />
          </div>

          <!-- Right Column (Sidebar) -->
          <div class="lg:sticky lg:top-0 lg:self-start print:mt-0 print:py-1">
            <div class="divide-y divide-n-slate-5 [&>*]:py-6">
              <!-- Pricing Total -->
              <EventPricingSidebar
                class="print:hidden"
                :redacted="!canViewPricing"
                :price-mode="canViewPricing ? priceMode : 'suggested'"
                :formatted-total="canViewPricing ? formattedSidebarTotal : null"
                :formatted-suggested="canViewPricing ? formattedSuggested : null"
                :formatted-impact="canViewPricing ? formattedImpact : null"
                :impact-delta="canViewPricing ? impactDelta : null"
                :loading="canViewPricing && loadingPrice"
              />

              <EventKeyInfo
                :locations="locationNames"
                :date="dateDisplay"
                :start-time="startTime"
                :end-time="endTime"
                :pax="pax"
                :editing-date="editingDate"
                :editing-time="editingTime"
                :temp-start-date="tempStartDate"
                :temp-start-time="tempStartTime"
                :temp-end-date="tempEndDate"
                :temp-end-time="tempEndTime"
                :can-edit-date-time="false"
                :editing-location="editingLocation"
                :temp-resource-id="tempResourceId"
                :resources="keyInfoResources"
                :can-edit-location="false"
                :saving="saving || savingKeyInfo"
                :collapsed="isSectionCollapsed('key_info').value"
                :read-only="isReadOnly"
                @toggle-collapse="toggleSection('key_info')"
                @start-edit-date="startEditingDate"
                @start-edit-time="startEditingTime"
                @save-date-time="saveDateTime"
                @cancel-edit-date="cancelEditingDate"
                @cancel-edit-time="cancelEditingTime"
                @update:temp-start-date="tempStartDate = $event"
                @update:temp-start-time="tempStartTime = $event"
                @update:temp-end-date="tempEndDate = $event"
                @update:temp-end-time="tempEndTime = $event"
                @start-edit-location="startEditingLocation"
                @save-location="saveLocation"
                @cancel-edit-location="cancelEditingLocation"
                @update:temp-resource-id="tempResourceId = $event"
              />

              <EventShijiBookingCard
                :event-id="eventId"
                :shiji-booking-link="shijiBookingLink"
                :read-only="isReadOnly"
                @refreshed="fetchEvent"
              />

              <EventContactInfo
                :customer="customer"
                :formatted-contacts="formattedContacts"
                :selected-customer="selectedCustomer"
                :show-customer-dropdown="showCustomerDropdown && canEditEvent"
                :searching-contacts="searchingContacts"
                :read-only="isReadOnly"
                @update-customer="updateCustomer"
                @open-customer-dropdown="openCustomerDropdown"
                @close-customer-dropdown="showCustomerDropdown = false"
                @search-customer="handleContactSearch"
              />

              <EventMembersInfo
                :event-manager="eventManager"
                :formatted-agents="formattedAgents"
                :selected-event-manager="selectedEventManager"
                :show-event-manager-dropdown="
                  showEventManagerDropdown && canManageEvent
                "
                :searching-agents="searchingAgents"
                :event-id="eventId"
                :collaborator-ids="collaboratorIds"
                :team-ids="assignedTeamIds"
                :can-manage-collaborators="canManageCollaborators"
                :can-manage-event-manager="canManageEvent"
                :read-only="isReadOnly"
                @update-event-manager="updateEventManager"
                @open-event-manager-dropdown="openEventManagerDropdown"
                @close-event-manager-dropdown="closeEventManagerDropdown"
                @search-event-manager="handleAgentSearch"
                @updated="updatedEvent => (event = updatedEvent)"
              />

              <EventSuppliersInfo
                v-if="suppliersEnabled"
                :event-id="eventId"
                :supplier-ids="supplierIds"
                :suppliers="assignedSuppliers"
                :can-manage="isAdmin"
                @updated="updatedEvent => (event = updatedEvent)"
              />

              <EventChangelogSidebar
                :event-id="eventId"
                :last-change="event?.last_change"
                @view-full="openHistoryDialog"
              />

              <!-- Conversations for the assigned contact -->
              <EventContactConversations
                class="print:hidden"
                flush
                :contact-id="customer?.id ?? null"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <EventTimeslotModal
      v-if="timeslotModalOpen"
      v-model="timeslotModalOpen"
      class="print:hidden"
      :allocation="editingTimeslot"
      :event="event"
      :spaces="accountSpaces"
      :resources="planningResources"
      :quotes="quotes"
      :is-loading="addingSlot"
      @save="handleSaveTimeslot"
    />

    <!-- Delete Confirmation Dialog -->
    <Dialog
      ref="deleteDialogRef"
      class="print:hidden"
      type="alert"
      :title="t('PLANNING.EVENTS.DELETE')"
      :description="
        t('PLANNING.EVENTS.DELETE_CONFIRMATION', { name: event?.name })
      "
      :cancel-button-label="t('PLANNING.EVENTS.CANCEL')"
      :confirm-button-label="t('PLANNING.EVENTS.DELETE')"
      width="sm"
      @confirm="confirmDelete"
    />

    <EventLostCancellationModal
      v-if="showLostCancellationModal"
      v-model="showLostCancellationModal"
      class="print:hidden"
      :event="pendingLostUpdate?.event || event"
      :is-loading="lostCancellationSaving"
      @confirm="confirmLostCancellation"
      @cancel="cancelLostCancellation"
    />

    <EventRescheduleModal
      v-if="showRescheduleModal"
      v-model="showRescheduleModal"
      class="print:hidden"
      :event="event"
      :is-loading="rescheduleSaving"
      @confirm="handleReschedule"
    />

    <EventDuplicateModal
      v-if="showDuplicateModal"
      v-model="showDuplicateModal"
      class="print:hidden"
      :event="event"
      :is-loading="duplicateSaving"
      @confirm="handleDuplicate"
    />

    <Dialog
      ref="deleteSlotDialogRef"
      class="print:hidden"
      type="alert"
      :title="t('PLANNING.SHOW.DELETE_SLOT_CONFIRM_TITLE')"
      :cancel-button-label="t('PLANNING.EVENTS.CANCEL')"
      :confirm-button-label="t('PLANNING.SHOW.DELETE_SLOT')"
      width="sm"
      @confirm="confirmDeleteAllocation"
    >
      <template #description>
        <div class="flex flex-col gap-2">
          <p class="mb-0 text-sm text-n-slate-11">
            {{
              t('PLANNING.SHOW.DELETE_SLOT_CONFIRM_DESCRIPTION', {
                title: pendingDeleteAllocation?.title || 'Untitled',
              })
            }}
          </p>
          <p
            v-if="pendingDeleteQuotedWarning"
            class="mb-0 text-sm text-amber-800 dark:text-amber-200"
          >
            {{ pendingDeleteQuotedWarning }}
          </p>
        </div>
      </template>
    </Dialog>

    <!-- Share Dialog -->
    <Dialog
      ref="shareDialogRef"
      class="print:hidden"
      :title="t('PLANNING.SHOW.SHARE_EVENT')"
      :show-confirm-button="false"
      :show-cancel-button="false"
      width="3xl"
    >
      <EventShareDialog
        :sharing-enabled="sharingEnabled"
        :share-url="shareUrl"
        :copying-link="copyingLink"
        :templates="eventPortals"
        :selected-template-id="selectedTemplateId"
        :event-id="eventId"
        @toggle-sharing="toggleSharing"
        @copy-share-link="copyShareLink"
        @regenerate-link="regenerateLink"
        @select-template="selectTemplate"
      />
    </Dialog>

    <!-- Changelog Dialog -->
    <EventChangelogDialog
      :is-open="isHistoryDialogOpen"
      :history-loading="historyLoading"
      :history-error="historyError"
      :history-records="historyRecords"
      :history-meta="historyMeta"
      :get-audit-changes="getAuditChanges"
      :action-label="actionLabel"
      :action-badge-class="actionBadgeClass"
      @close="closeHistoryDialog"
      @update:is-open="val => !val && closeHistoryDialog()"
      @page-change="onHistoryPageChange"
    />

    <QuoteBuilderModal
      v-if="canViewPricing && quotesEnabled && showBuilder"
      v-model="showBuilder"
      :source-breakdown="activeBreakdown"
      :contact-id="event?.customer_id"
      :company-id="event?.customer?.company_id"
      :saving="quoteBuilderSaving"
      :next-version="
        quotes.length
          ? Math.max(...quotes.map(q => Number(q.version) || 0)) + 1
          : 1
      "
      :seed-quote="builderSeedQuote"
      :event-name="event?.name || ''"
      :event-start-date="event?.start_date || null"
      :event-manager-email="
        eventManager?.email || event?.event_manager?.email || ''
      "
      :event-manager-phone="
        eventManager?.phone_number || event?.event_manager?.phone || ''
      "
      :has-event-manager="Boolean(event?.event_manager_id)"
      @close="closeBuilder"
      @save="saveQuoteFromBuilder"
    />

    <QuoteSendDialog
      v-if="canViewPricing && quotesEnabled && showSendDialog"
      v-model="showSendDialog"
      :event-id="eventId"
      :quote="sendQuoteTarget"
      @close="closeSendDialog"
      @sent="onQuoteSent"
    />
  </div>

  <!-- Loading State -->
  <div
    v-else
    class="flex items-center justify-center h-full bg-white dark:bg-slate-900"
  >
    <div class="flex flex-col items-center">
      <div
        class="w-8 h-8 border-4 border-woot-500 border-t-transparent rounded-full animate-spin mb-3"
      />
      <p class="text-sm text-slate-500">
        {{ t('PLANNING.SHOW.LOADING_EVENT') }}
      </p>
    </div>
  </div>
</template>
